/**
 * Compact run-detail header: title/status, timing, target environment, and actions.
 *
 * @module ui/pages/RunDetailPage/RunDetailHeader
 */

import type { ReactNode } from 'react';
import {
  StatusBadge,
  TagBadge,
  Text,
  hashStringToTagColor,
  normalizeRunStatus,
} from '@chatsdet/ui';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { RecordedWindow } from '../../components/RecordedWindow';

type RunEnvironment = {
  name: string;
  type: string;
};

type RunDetailHeaderProps = {
  title: string;
  status: string;
  statusLabel: string;
  startedAt: string | null;
  finishedAt: string | null;
  isRunning: boolean;
  environment?: RunEnvironment | null;
  environmentInstanceUrl?: string | null;
  sandbox?: { id: string; name: string; status: string } | null;
  /** Framework tags (e.g. Gherkin `@tags`); omitted or empty renders nothing. */
  tags?: string[] | null;
  actions: ReactNode;
};

const formatEnvironmentLabel = (environment: RunEnvironment): string => {
  const name = environment.name.trim();
  const type = environment.type.trim();
  if (!type) return name;
  if (name.toLocaleLowerCase() === type.toLocaleLowerCase()) return type.toLocaleLowerCase();

  const duplicateSuffix = `(${type})`;
  const baseName = name.toLocaleLowerCase().endsWith(duplicateSuffix.toLocaleLowerCase())
    ? name.slice(0, -duplicateSuffix.length).trimEnd()
    : name;

  return `${baseName} ${type.toLocaleLowerCase()}`;
};

export function RunDetailHeader({
  title,
  status,
  statusLabel,
  startedAt,
  finishedAt,
  isRunning,
  environment,
  environmentInstanceUrl,
  sandbox,
  tags,
  actions,
}: RunDetailHeaderProps) {
  const environmentLabel = environment ? formatEnvironmentLabel(environment) : null;

  return (
    <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          <Text weight="semibold" className="min-w-0 truncate" title={title}>
            {title}
          </Text>
          <StatusBadge
            status={status === 'flaky_success' ? 'flaky' : normalizeRunStatus(status)}
            label={statusLabel}
            className="shrink-0"
          />
          {tags && tags.length > 0 ? (
            <div className="flex shrink-0 flex-wrap items-center gap-1">
              {tags.map((tag) => (
                <TagBadge key={tag} name={tag} color={hashStringToTagColor(tag)} size="sm" />
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
          <RecordedWindow startedAt={startedAt} finishedAt={finishedAt} isRunning={isRunning} />
          {(environmentLabel || environmentInstanceUrl || sandbox) && (
            <span aria-hidden="true">·</span>
          )}
          {environmentLabel && environmentInstanceUrl ? (
            <a
              href={environmentInstanceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={`${environmentLabel} (opens in a new tab)`}
              className="inline-flex items-center gap-1 font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
            >
              {environmentLabel}
              <ArrowSquareOut size={12} aria-hidden="true" />
            </a>
          ) : environmentLabel ? (
            <span className="font-medium text-foreground">{environmentLabel}</span>
          ) : environmentInstanceUrl ? (
            <a
              href={environmentInstanceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="Sandbox (opens in a new tab)"
              className="inline-flex items-center gap-1 font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
            >
              sandbox
              <ArrowSquareOut size={12} aria-hidden="true" />
            </a>
          ) : null}
          {sandbox && (
            <>
              {(environmentLabel || environmentInstanceUrl) && <span aria-hidden="true">·</span>}
              <a
                href={`/sandboxes/${sandbox.id}`}
                aria-label={`Sandbox ${sandbox.name}`}
                title={`Sandbox ${sandbox.id} · ${sandbox.status}. Live logs: cofactor sandbox logs ${sandbox.id}`}
                className="font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
              >
                {sandbox.name} · {sandbox.id.slice(0, 8)} · {sandbox.status}
              </a>
            </>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">{actions}</div>
    </header>
  );
}
